/**
 * Canvas Layout - Reports
 *
 * Defines the route identifier and fallback canvas configuration
 * for the general-purpose Reports page available to all customers.
 *
 * The default canvas is intentionally minimal. Data sources and widgets
 * are added incrementally as the feature matures.
 *
 * Organisations that need a custom layout can have one saved to the backend
 * against the route key "reports" by an admin — the fallback config is only
 * used when no backend layout exists for that organisation.
 */

import { WidgetType, type CanvasConfig, type TabLayoutProps } from "~/helpers/canvas/canvas-types";
import { AssetsSources } from "~/helpers/canvas/sources/asset-sources";
import { useQuery } from "@vue/apollo-composable";
import {
  CanvasLayoutsForRouteDocument,
  CanvasLayoutFieldsFragmentDoc,
  CanvasLayoutType,
  type CreateCanvasLayoutInput,
} from "~/graphql/generated/graphql";
import { useFragment } from "~/graphql/generated";
import { transformBackendLayout } from "~/composables/queries/useCanvasLayoutQuery";
import { transformConfigToInput } from "~/composables/useCanvasLayoutTransform";
import { useCanvasLayoutMutations } from "~/composables/queries/useCanvasLayoutMutations";

/** Route identifier used to fetch the backend canvas layout */
export const REPORTS_CANVAS_ROUTE = "reports";

/** Represents one selectable canvas layout (backend or fallback) */
export interface ReportsCanvasOption {
  id: string;
  name: string;
  isDefault: boolean;
  isSelected: boolean;
  isShared: boolean;
  config: CanvasConfig;
}

/** Colors matching the property overview dashboard palette */
const CHART_COLORS = ["--color-accent-800", "--color-blue-300", "--color-green-300", "--color-red-300", "--color-yellow-300"];

/**
 * Composable that provides the canvas layout configuration for the Reports page.
 * Returns the route identifier, fallback config, and backend layout controls.
 */
export const useReportsCanvas = () => {
  const t = useTranslator();

  const fallbackConfig = computed<CanvasConfig>(() => {
    return {
      id: "reports-canvas",
      layoutType: "TAB",
      name: "Align Standard Report",
      layoutProps: {
        tabs: [
          { label: "Overview", canvasIndex: 0 },
          { label: "Vacancy", canvasIndex: 1 },
          { label: t("CANVAS_TAB_RENT"), canvasIndex: 2 },
        ],
      } satisfies TabLayoutProps,
      variables: {
        dateFrom: { source: "static", value: "2021-03-01" },
        dateTo: { source: "computed", value: "today" },
        groupIds: { source: "filter", key: "portfolioGroupIds" },
      },
      canvases: [
        {
          name: "Overview",
          widgets: [
            // ── Filters row ──────────────────────────────────────────────────────
            {
              name: "filter-date-range",
              type: WidgetType.FilterDateRange,
              key: "dateRange",
              label: t("CANVAS_FILTER_DATE_RANGE"),
              position: { column: 1, row: 1, columnSpan: 4, rowSpan: 1 },
              defaultValue: { from: "2021-03-01", to: "today" },
            },
            {
              name: "filter-portfolio",
              type: WidgetType.FilterDropdownMultiple,
              key: "portfolioGroupIds",
              label: t("CANVAS_FILTER_PORTFOLIO"),
              optionsSource: "assets.portfolios",
              placeholder: t("CANVAS_FILTER_ALL_PORTFOLIOS"),
              position: { column: 5, row: 1, columnSpan: 3, rowSpan: 1 },
              defaultSelectFirst: true,
              defaultValue: [],
            },
            {
              name: "filter-assets",
              type: WidgetType.FilterDropdownMultiple,
              key: "assetIds",
              label: t("CANVAS_FILTER_ASSETS"),
              optionsSource: "portfolio.properties",
              placeholder: t("CANVAS_FILTER_ALL_ASSETS"),
              position: { column: 8, row: 1, columnSpan: 3, rowSpan: 1 },
              defaultValue: [],
            },
            {
              name: "filter-time-range",
              type: WidgetType.FilterDropdown,
              key: "timeRange",
              label: t("CANVAS_FILTER_TIME_GRAIN"),
              position: { column: 11, row: 1, columnSpan: 2, rowSpan: 1 },
              options: [
                { value: "monthly", label: t("CANVAS_FILTER_OPTION_MONTHLY") },
                { value: "quarterly", label: t("CANVAS_FILTER_OPTION_QUARTERLY") },
                { value: "yearly", label: t("CANVAS_FILTER_OPTION_YEARLY") },
              ],
              defaultValue: "monthly",
            },
            // ── Charts ───────────────────────────────────────────────────────────
            {
              name: "widget-churn-rate-rolling-12m",
              label: t("CANVAS_WIDGET_CHURN_ROLLING_12M"),
              type: WidgetType.Chart,
              chartType: "line",
              dataSource: AssetsSources.CHURN_RATE_ROLLING_12M_SUM,
              yAxisFormat: "percent",
              yAxisDecimals: 1,
              showLegend: false,
              fillArea: true,
              colors: CHART_COLORS,
              height: "280px",
              position: { column: 1, row: 2, columnSpan: 12, rowSpan: 4 },
              filters: ["dateRange", "assetIds", "timeRange"],
            },
            {
              name: "widget-churn-rate-monthly",
              label: t("CANVAS_WIDGET_CHURN_MONTHLY"),
              type: WidgetType.Chart,
              chartType: "bar",
              dataSource: AssetsSources.CHURN_RATE_MONTHLY,
              yAxisFormat: "percent",
              yAxisDecimals: 1,
              showLegend: false,
              colors: CHART_COLORS,
              height: "280px",
              position: { column: 1, row: 6, columnSpan: 12, rowSpan: 4 },
              filters: ["dateRange", "assetIds", "timeRange"],
            },
          ],
        },
        {
          name: "Vacancy",
          widgets: [
            // ── Filters row ──────────────────────────────────────────────────────
            {
              name: "filter-date-range-vac",
              type: WidgetType.FilterDateRange,
              key: "dateRange",
              label: t("CANVAS_FILTER_DATE_RANGE"),
              position: { column: 1, row: 1, columnSpan: 3, rowSpan: 1 },
              defaultValue: { from: "2021-03-01", to: "today" },
            },
            {
              name: "filter-portfolio-vac",
              type: WidgetType.FilterDropdownMultiple,
              key: "portfolioGroupIds",
              label: t("CANVAS_FILTER_PORTFOLIO"),
              optionsSource: "assets.portfolios",
              placeholder: t("CANVAS_FILTER_ALL_PORTFOLIOS"),
              position: { column: 4, row: 1, columnSpan: 2, rowSpan: 1 },
              defaultSelectFirst: true,
              defaultValue: [],
            },
            {
              name: "filter-assets-vac",
              type: WidgetType.FilterDropdownMultiple,
              key: "assetIds",
              label: t("CANVAS_FILTER_ASSETS"),
              optionsSource: "portfolio.properties",
              placeholder: t("CANVAS_FILTER_ALL_ASSETS"),
              position: { column: 6, row: 1, columnSpan: 3, rowSpan: 1 },
              defaultValue: [],
            },
            {
              name: "filter-type-groups-vac",
              type: WidgetType.FilterDropdownMultiple,
              key: "typeGroups",
              label: t("CANVAS_FILTER_USAGE_TYPES"),
              placeholder: t("CANVAS_FILTER_ALL_TYPE_GROUPS"),
              position: { column: 9, row: 1, columnSpan: 2, rowSpan: 1 },
              options: [
                { value: "RESIDENTIAL", label: "TYPE_GROUP_RESIDENTIAL", labelIsTranslationKey: true },
                { value: "COMMERCIAL", label: "TYPE_GROUP_COMMERCIAL", labelIsTranslationKey: true },
                { value: "OTHER", label: "TYPE_GROUP_OTHER", labelIsTranslationKey: true },
              ],
              defaultValue: [],
            },
            {
              name: "filter-time-range-vac",
              type: WidgetType.FilterDropdown,
              key: "timeRange",
              label: t("CANVAS_FILTER_TIME_GRAIN"),
              position: { column: 11, row: 1, columnSpan: 2, rowSpan: 1 },
              options: [
                { value: "monthly", label: t("CANVAS_FILTER_OPTION_MONTHLY") },
                { value: "quarterly", label: t("CANVAS_FILTER_OPTION_QUARTERLY") },
                { value: "yearly", label: t("CANVAS_FILTER_OPTION_YEARLY") },
              ],
              defaultValue: "monthly",
            },
            // ── Charts ───────────────────────────────────────────────────────────
            {
              name: "widget-vacancy-rate",
              label: t("CANVAS_WIDGET_VACANCY_RATE_AREA"),
              type: WidgetType.Chart,
              chartType: "line",
              dataSource: AssetsSources.VACANCY_RATE,
              yAxisFormat: "percent",
              yAxisDecimals: 1,
              showLegend: true,
              fillArea: true,
              colors: CHART_COLORS,
              height: "280px",
              position: { column: 1, row: 2, columnSpan: 12, rowSpan: 4 },
              filters: ["dateRange", "assetIds", "typeGroups", "timeRange"],
            },
            {
              name: "widget-vacancy-rate-nominel",
              label: t("CANVAS_WIDGET_VACANCY_RATE_ECONOMIC"),
              type: WidgetType.Chart,
              chartType: "line",
              dataSource: AssetsSources.VACANCY_RATE_NOMINEL,
              yAxisFormat: "currency",
              showLegend: true,
              fillArea: true,
              colors: CHART_COLORS,
              height: "280px",
              position: { column: 1, row: 6, columnSpan: 12, rowSpan: 4 },
              filters: ["dateRange", "assetIds", "typeGroups", "timeRange"],
            },
          ],
        },
        {
          name: t("CANVAS_TAB_RENT"),
          widgets: [
            // ── Filters row ──────────────────────────────────────────────────────
            {
              name: "filter-date-range-rent",
              type: WidgetType.FilterDateRange,
              key: "dateRange",
              label: t("CANVAS_FILTER_DATE_RANGE"),
              position: { column: 1, row: 1, columnSpan: 3, rowSpan: 1 },
              defaultValue: { from: "2021-03-01", to: "today" },
            },
            {
              name: "filter-portfolio-rent",
              type: WidgetType.FilterDropdownMultiple,
              key: "portfolioGroupIds",
              label: t("CANVAS_FILTER_PORTFOLIO"),
              optionsSource: "assets.portfolios",
              placeholder: t("CANVAS_FILTER_ALL_PORTFOLIOS"),
              position: { column: 4, row: 1, columnSpan: 2, rowSpan: 1 },
              defaultSelectFirst: true,
              defaultValue: [],
            },
            {
              name: "filter-assets-rent",
              type: WidgetType.FilterDropdownMultiple,
              key: "assetIds",
              label: t("CANVAS_FILTER_ASSETS"),
              optionsSource: "portfolio.properties",
              placeholder: t("CANVAS_FILTER_ALL_ASSETS"),
              position: { column: 6, row: 1, columnSpan: 3, rowSpan: 1 },
              defaultValue: [],
            },
            {
              name: "filter-type-groups-rent",
              type: WidgetType.FilterDropdownMultiple,
              key: "typeGroups",
              label: t("CANVAS_FILTER_USAGE_TYPES"),
              placeholder: t("CANVAS_FILTER_ALL_TYPE_GROUPS"),
              position: { column: 9, row: 1, columnSpan: 2, rowSpan: 1 },
              options: [
                { value: "RESIDENTIAL", label: "TYPE_GROUP_RESIDENTIAL", labelIsTranslationKey: true },
                { value: "COMMERCIAL", label: "TYPE_GROUP_COMMERCIAL", labelIsTranslationKey: true },
                { value: "OTHER", label: "TYPE_GROUP_OTHER", labelIsTranslationKey: true },
              ],
              defaultValue: [],
            },
            {
              name: "filter-time-range-rent",
              type: WidgetType.FilterDropdown,
              key: "timeRange",
              label: t("CANVAS_FILTER_TIME_GRAIN"),
              position: { column: 11, row: 1, columnSpan: 2, rowSpan: 1 },
              options: [
                { value: "monthly", label: t("CANVAS_FILTER_OPTION_MONTHLY") },
                { value: "quarterly", label: t("CANVAS_FILTER_OPTION_QUARTERLY") },
                { value: "yearly", label: t("CANVAS_FILTER_OPTION_YEARLY") },
              ],
              defaultValue: "monthly",
            },
            // ── Charts ───────────────────────────────────────────────────────────
            {
              name: "widget-actual-rent",
              label: t("CANVAS_WIDGET_ACTUAL_RENT"),
              type: WidgetType.Chart,
              chartType: "line",
              dataSource: AssetsSources.ACTUAL_RENT,
              yAxisFormat: "currency",
              showLegend: true,
              fillArea: true,
              colors: CHART_COLORS,
              height: "280px",
              position: { column: 1, row: 2, columnSpan: 12, rowSpan: 4 },
              filters: ["dateRange", "assetIds", "typeGroups", "timeRange"],
            },
            {
              name: "widget-square-meter-rent",
              label: t("CANVAS_WIDGET_SQUARE_METER_RENT"),
              type: WidgetType.Chart,
              chartType: "line",
              dataSource: AssetsSources.SQUARE_METER_RENT,
              yAxisFormat: "currency",
              showLegend: true,
              fillArea: true,
              colors: CHART_COLORS,
              height: "280px",
              position: { column: 1, row: 6, columnSpan: 12, rowSpan: 4 },
              filters: ["dateRange", "assetIds", "typeGroups", "timeRange"],
            },
          ],
        },
      ],
    } as CanvasConfig;
  });

  const query = useQuery(CanvasLayoutsForRouteDocument, { route: REPORTS_CANVAS_ROUTE }, { fetchPolicy: "cache-first" });

  /** All backend layouts transformed to CanvasConfig, with metadata */
  const backendOptions = computed<ReportsCanvasOption[]>(() => {
    const raw = query.result.value?.canvasLayoutsForRoute ?? [];
    return raw.map((entry) => {
      const layout = useFragment(CanvasLayoutFieldsFragmentDoc, entry);
      return {
        id: layout.id,
        name: layout.name || layout.id,
        isDefault: layout.isDefault,
        isSelected: layout.isSelected,
        isShared: layout.isShared,
        config: transformBackendLayout(layout),
      };
    });
  });

  /** Fallback option — only marked as default when no backend layout holds that flag */
  const fallbackOption = computed<ReportsCanvasOption>(() => ({
    id: "fallback",
    name: fallbackConfig.value.name || "Default",
    isDefault: !backendOptions.value.some((o) => o.isDefault),
    isSelected: false,
    isShared: false,
    config: fallbackConfig.value,
  }));

  /** All available options — backend layouts plus the built-in fallback */
  const canvasOptions = computed<ReportsCanvasOption[]>(() => {
    return [...backendOptions.value, fallbackOption.value];
  });

  const loading = query.loading;

  /**
   * Resolve a canvas config by layout ID.
   * Falls back to the default option, then first option, then fallbackConfig.
   */
  function getCanvasConfigById(layoutId: string | null | undefined): CanvasConfig | null {
    if (loading.value) return null;
    if (layoutId) {
      const match = canvasOptions.value.find((o) => o.id === layoutId);
      if (match) return match.config;
    }
    const def = canvasOptions.value.find((o) => o.isDefault);
    return def?.config ?? canvasOptions.value[0]?.config ?? fallbackConfig.value;
  }

  // --- Mutations ---

  const mutations = useCanvasLayoutMutations();

  const queryVariables = { route: REPORTS_CANVAS_ROUTE };

  /** Create a new blank (or default-based) canvas layout for the reports route */
  async function createLayout(name: string, opts?: { isDefault?: boolean; isShared?: boolean; startConfig?: CanvasConfig }) {
    const startConfig = opts?.startConfig;
    const transformed = startConfig ? transformConfigToInput(startConfig) : null;
    const input: CreateCanvasLayoutInput = {
      route: REPORTS_CANVAS_ROUTE,
      name,
      layoutType: transformed?.layoutType ?? CanvasLayoutType.Tab,
      ...(transformed?.layoutProps ? { layoutProps: transformed.layoutProps } : {}),
      canvases: transformed?.canvases ?? [{ name: "Tab 1", widgets: [] }],
      isDefault: opts?.isDefault ?? false,
      isShared: opts?.isShared ?? false,
    };
    await mutations.createLayout(input, {
      update: (cache, { data }) => {
        if (!data?.createCanvasLayout) return;
        const existing = cache.readQuery({ query: CanvasLayoutsForRouteDocument, variables: queryVariables });
        cache.writeQuery({
          query: CanvasLayoutsForRouteDocument,
          variables: queryVariables,
          data: {
            canvasLayoutsForRoute: [...(existing?.canvasLayoutsForRoute ?? []), data.createCanvasLayout],
          },
        });
      },
    });
  }

  /** Rename a canvas layout without changing its config */
  async function renameLayout(id: string, newName: string) {
    const option = backendOptions.value.find((o) => o.id === id);
    if (!option) return;
    await mutations.updateLayout(id, { ...option.config, name: newName }, {
      update: (cache, { data }) => {
        if (!data?.updateCanvasLayout) return;
        const existing = cache.readQuery({ query: CanvasLayoutsForRouteDocument, variables: queryVariables });
        if (!existing) return;
        cache.writeQuery({
          query: CanvasLayoutsForRouteDocument,
          variables: queryVariables,
          data: {
            canvasLayoutsForRoute: existing.canvasLayoutsForRoute.map((entry) =>
              cache.identify(entry) === cache.identify({ __typename: "CanvasLayout", id })
                ? data.updateCanvasLayout
                : entry,
            ),
          },
        });
      },
    });
  }

  /** Update an existing canvas layout's config */
  async function updateLayout(id: string, config: CanvasConfig) {
    await mutations.updateLayout(id, config, {
      update: (cache, { data }) => {
        if (!data?.updateCanvasLayout) return;
        const existing = cache.readQuery({ query: CanvasLayoutsForRouteDocument, variables: queryVariables });
        if (!existing) return;
        cache.writeQuery({
          query: CanvasLayoutsForRouteDocument,
          variables: queryVariables,
          data: {
            canvasLayoutsForRoute: existing.canvasLayoutsForRoute.map((entry) =>
              cache.identify(entry) === cache.identify({ __typename: "CanvasLayout", id })
                ? data.updateCanvasLayout
                : entry,
            ),
          },
        });
      },
    });
  }

  /** Delete a canvas layout by id */
  async function deleteLayout(id: string) {
    await mutations.deleteLayout(id, {
      update: (cache, { data }) => {
        if (!data?.deleteCanvasLayout) return;
        const existing = cache.readQuery({ query: CanvasLayoutsForRouteDocument, variables: queryVariables });
        if (!existing) return;
        cache.writeQuery({
          query: CanvasLayoutsForRouteDocument,
          variables: queryVariables,
          data: {
            canvasLayoutsForRoute: existing.canvasLayoutsForRoute.filter(
              (entry) => cache.identify(entry) !== cache.identify({ __typename: "CanvasLayout", id }),
            ),
          },
        });
      },
    });
  }

  /** Mark a canvas layout as the org-wide default */
  async function markAsDefault(id: string) {
    await mutations.markAsDefault(id, {
      update: (cache, { data }) => {
        if (!data?.markCanvasLayoutAsDefault) return;
        // The server clears isDefault on all other layouts — mirror that in cache
        backendOptions.value.forEach((opt) => {
          if (opt.id !== id && opt.isDefault) {
            cache.modify({
              id: cache.identify({ __typename: "CanvasLayout", id: opt.id }),
              fields: { isDefault: () => false },
            });
          }
        });
      },
    });
  }

  /** Set a canvas layout as the current user's preference (overrides default) */
  async function setUserPreference(id: string) {
    await mutations.setUserPreference(id, {
      update: (cache, { data }) => {
        if (!data?.setUserCanvasLayoutPreference) return;
        // The server clears isSelected on all other layouts — mirror that in cache
        backendOptions.value.forEach((opt) => {
          if (opt.id !== id && opt.isSelected) {
            cache.modify({
              id: cache.identify({ __typename: "CanvasLayout", id: opt.id }),
              fields: { isSelected: () => false },
            });
          }
        });
      },
    });
  }

  /** Toggle shared status of a canvas layout */
  async function toggleShared(id: string, currentlyShared: boolean) {
    if (currentlyShared) {
      await mutations.unshareLayout(id);
    } else {
      await mutations.shareLayout(id);
    }
    // Share/unshare returns the updated layout — Apollo auto-updates by id
  }

  return {
    route: REPORTS_CANVAS_ROUTE,
    fallbackConfig,
    loading,
    canvasOptions,
    getCanvasConfigById,
    createLayout,
    renameLayout,
    updateLayout,
    deleteLayout,
    markAsDefault,
    setUserPreference,
    toggleShared,
    mutating: mutations.mutating,
  };
};
